/***
 **Module Name: RefundBookingModal
 **File Name :  RefundBookingModal.jsx
 **Project :  SPOT SAVER
 **Copyright(c) : SPOT SAVER.
 **Organization : SPOT SAVER
 **author :  Rajesh
 **license :
 **version :  1.0.0
 **Created on : Oct 05 2026
 **Last modified on:
 **Description : The Refund window on Finance > Transactions > Visitors.
 **
 **              The API (POST /admin/refundBooking) decides everything that matters:
 **              what can still be refunded, the venue's exact share, whether the booking
 **              is cancelled. This window only collects the admin's choice and says
 **              plainly what will happen, before anything moves:
 **                - who pays: by default the venue's share comes back with the refund;
 **                  "SpotSaver covers it" is for refunds that are SpotSaver's fault;
 **                - a full refund cancels a booking nobody has checked in to yet;
 **                - the visitor and the venue are emailed.
 ***/
import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import tmdbApi from "../../api/tmdbApi";

// spot-admin.css palette, as in StripeModeCard.
const HEADING = "#4F378A";
const MUTED = "#5C6188";
const BORDER = "#E2E8F0";
const PANEL = "#FAFBFE";
const DANGER = "#DC5354";

export const REASONS = [
    { value: "requested_by_customer", label: "Requested by the customer" },
    { value: "duplicate", label: "Duplicate charge" },
    { value: "fraudulent", label: "Fraudulent" },
];

const money = (n) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;
const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/** What is still refundable on a row, in dollars. */
export const remainingOf = (row) => Math.max(0, toNum(row?.totalAmount) - toNum(row?.refundedAmount));

const RefundBookingModal = ({ row, locationName, clientName, onClose, onRefunded, onSessionExpired }) => {
    const remaining = remainingOf(row);
    const [amount, setAmount] = useState(remaining.toFixed(2));
    const [reason, setReason] = useState("requested_by_customer");
    const [note, setNote] = useState("");
    const [venuePays, setVenuePays] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const amt = toNum(amount);
    const amountOk = amt > 0 && Math.round(amt * 100) <= Math.round(remaining * 100) && /^\d+(\.\d{1,2})?$/.test(String(amount).trim());
    const valid = amountOk && note.trim().length > 0 && !submitting;
    const full = amountOk && Math.round(amt * 100) === Math.round(remaining * 100);

    // An estimate for the admin: Stripe reverses the venue's transfer in proportion to the
    // refund, and the API reports the exact figure afterwards.
    const venueShare = useMemo(() => {
        const total = toNum(row?.totalAmount);
        const clientShare = toNum(row?.clientPayoutAmount);
        return venuePays && total > 0 ? (amt * clientShare) / total : 0;
    }, [amt, venuePays, row]);

    const submit = async () => {
        if (!valid) return;
        setSubmitting(true);
        setError("");
        try {
            const res = await tmdbApi.refundBooking({
                transactionId: row.transactionId,
                amount: amt,
                reason,
                note: note.trim(),
                venuePays,
            });
            const result = res?.result;
            if (result === "Invalid token or Expired") {
                onSessionExpired();
                return;
            }
            if (result?.success) {
                onRefunded(result);
                return;
            }
            setError(result?.message || res?.error || "The refund could not be issued.");
        } catch (err) {
            setError(err?.response?.data?.error || err?.message || "The refund could not be issued.");
        } finally {
            setSubmitting(false);
        }
    };

    const labelStyle = { color: MUTED, fontSize: 13, fontWeight: 600 };
    const fieldStyle = { borderRadius: 10, border: `1px solid ${BORDER}`, background: PANEL };

    return (
        <Modal show centered backdrop="static" onHide={() => !submitting && onClose()} id="refund-booking-modal">
            <div className="modal-body px-4 pt-4 pb-2">
                <h3 style={{ fontSize: 20, fontWeight: 700, color: HEADING }}>Refund this booking</h3>
                <p style={{ color: MUTED, fontSize: 13, marginBottom: 16 }}>
                    {locationName || "Unknown location"}{clientName ? ` · ${clientName}` : ""}
                    <br />
                    Transaction {row?.transactionId} · paid {money(toNum(row?.totalAmount))}
                    {toNum(row?.refundedAmount) > 0 ? ` · ${money(toNum(row.refundedAmount))} already refunded` : ""}
                </p>

                <label htmlFor="refund-amount" className="form-label" style={labelStyle}>
                    Amount (up to {money(remaining)})
                </label>
                <div className="input-group mb-3">
                    <span className="input-group-text" style={fieldStyle}>$</span>
                    <input
                        id="refund-amount"
                        type="text"
                        inputMode="decimal"
                        className={`form-control ${amount !== "" && !amountOk ? "is-invalid" : ""}`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        style={fieldStyle}
                    />
                </div>

                <label htmlFor="refund-reason" className="form-label" style={labelStyle}>Reason</label>
                <select id="refund-reason" className="form-select mb-3" value={reason} onChange={(e) => setReason(e.target.value)} style={fieldStyle}>
                    {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>

                <div className="mb-3" role="radiogroup" aria-label="Who pays for this refund">
                    <div style={labelStyle} className="mb-1">Who pays</div>
                    <div className="form-check">
                        <input className="form-check-input" type="radio" id="refund-venue-pays" checked={venuePays} onChange={() => setVenuePays(true)} />
                        <label className="form-check-label" htmlFor="refund-venue-pays" style={{ fontSize: 14 }}>
                            Take the venue's share back <span style={{ color: MUTED }}>(the venue's fault)</span>
                        </label>
                    </div>
                    <div className="form-check">
                        <input className="form-check-input" type="radio" id="refund-spotsaver-pays" checked={!venuePays} onChange={() => setVenuePays(false)} />
                        <label className="form-check-label" htmlFor="refund-spotsaver-pays" style={{ fontSize: 14 }}>
                            SpotSaver covers it <span style={{ color: MUTED }}>(SpotSaver's fault)</span>
                        </label>
                    </div>
                </div>

                <label htmlFor="refund-note" className="form-label" style={labelStyle}>
                    Note <span style={{ fontWeight: 400 }}>(required, kept with the refund, not sent to anyone)</span>
                </label>
                <textarea
                    id="refund-note"
                    className="form-control mb-3"
                    rows={2}
                    maxLength={500}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Venue closed early, guest turned away"
                    style={fieldStyle}
                />

                {amountOk && (
                    <div className="p-3 rounded-3 mb-3" id="refund-summary" style={{ background: PANEL, border: `1px solid ${BORDER}`, fontSize: 13, color: MUTED }}>
                        <div>The visitor gets <strong style={{ color: HEADING }}>{money(amt)}</strong> back to their card.</div>
                        {venuePays ? (
                            <div>About <strong style={{ color: HEADING }}>{money(venueShare)}</strong> comes back from {clientName || "the venue"}'s payout; SpotSaver returns the rest.</div>
                        ) : (
                            <div>SpotSaver pays all of it. The venue's payout is not touched.</div>
                        )}
                        {full && <div>This is a full refund: if nobody has checked in yet, the booking is cancelled and its spot released.</div>}
                        <div>The visitor and the venue are emailed.</div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger py-2 mb-2" role="alert" id="refund-error" style={{ fontSize: 13 }}>
                        {error}
                    </div>
                )}
            </div>
            <div className="modal-footer border-0 justify-content-center gap-3 pb-4">
                <button className="stroke-btn-style" type="button" id="refund-cancel-btn" style={{ textTransform: "none" }} disabled={submitting} onClick={onClose}>
                    Cancel
                </button>
                <button
                    className="fill-btn-style"
                    type="button"
                    id="refund-confirm-btn"
                    disabled={!valid}
                    style={{
                        textTransform: "none",
                        background: DANGER,
                        borderColor: DANGER,
                        opacity: valid ? 1 : 0.45,
                        cursor: valid ? "pointer" : "not-allowed",
                    }}
                    onClick={submit}
                >
                    {submitting ? "Refunding…" : amountOk ? `Refund ${money(amt)}` : "Refund"}
                </button>
            </div>
        </Modal>
    );
};

export default RefundBookingModal;
